import {
  logOut,
  signIn,
  recoverPassword,
  resetPassword,
  userUpdate,
  markCalloutSeen,
  requestSignInToken,
  verifySignInToken,
  demoSignIn,
} from '../api/users';
import { updateProfile } from '../api/profiles';
import { impersonateUser, stopImpersonating } from '../api/v2/super_users/sessions.js';
import { anonymizeFullStory, identifyFullStoryUser } from './fullstory';

export default class Auth {
  constructor(user) {
    this.currentUser = user;
    this.initializePendo();
    identifyFullStoryUser(this.currentUser);
  }

  getCurrentUser = () => {
    return this.currentUser;
  };

  currentAccount = () => {
    if (!this.isLoggedIn() || !this.currentUser) {
      return null;
    }

    return this.currentUser.account || null;
  };

  initializePendo = () => {
    if (!this.currentUser) {
      return;
    }

    if (typeof pendo === 'undefined') {
      return;
    }

    pendo.initialize({
      visitor: {
        id: this.currentUser.id,
        email: this.currentUser.email,
        full_name: this.currentUser.name,
        role: this.currentUser.current_profile_type,

        // You can add any additional visitor level key-values here,
        // as long as it's not one of the above reserved names.
      },
    });
  };

  isLoggedIn = () => {
    var currentUserPresent = Object.keys(this.currentUser).length !== 0;

    if (!currentUserPresent) {
      return false;
    }

    if (this.getCurrentUser().current_profile === null) {
      return false;
    }

    return true;
  };

  userLogOut = () => {
    logOut(() => {
      anonymizeFullStory();
      this.currentUser = {};
      window.location = '/member/login';
    });
  };

  userSignIn = (email, password, rememberMe, onSuccess, onTwoFactorRequired, onError, onPasswordlessRequired) => {
    signIn(
      email,
      password,
      rememberMe,
      (data) => {
        this.currentUser = data.user;
        this.initializePendo();
        identifyFullStoryUser(this.currentUser);
        onSuccess(data);
      },
      onTwoFactorRequired,
      onError,
      onPasswordlessRequired
    );
  };

  userRecoverPassword = (email, onSuccess, onError) => {
    recoverPassword(email, onSuccess, onError);
  };

  userRequestSignInToken = (email, onSuccess, onError) => {
    requestSignInToken(email, onSuccess, onError);
  };

  userVerifySignInToken = (code, onSuccess, onError) => {
    verifySignInToken(
      code,
      (data) => {
        this.currentUser = data.user;
        this.initializePendo();
        identifyFullStoryUser(this.currentUser);
        onSuccess(data);
      },
      onError
    );
  };

  userDemoSignIn = async ({ token, email }, onSuccess, onError) => {
    try {
      const data = await demoSignIn({ token, email });
      if (data.success) {
        this.currentUser = data.user;
        this.initializePendo();
        identifyFullStoryUser(this.currentUser);
        onSuccess(data);
      } else if (onError) {
        onError([data.error || 'Unable to sign in.']);
      }
    } catch (_e) {
      if (onError) {
        onError(['Network error. Please check your connection and try again.']);
      }
    }
  };

  userResetPassword = (resetPasswordToken, password, onSuccess, onTwoFactorRequired, onError) => {
    resetPassword(
      resetPasswordToken,
      password,
      (user) => {
        this.currentUser = user;
        identifyFullStoryUser(this.currentUser);
        if (onSuccess) {
          onSuccess(user);
        }
      },
      (data) => {
        this.currentUser = {};
        if (onTwoFactorRequired) {
          onTwoFactorRequired(data);
        }
      },
      (errors) => {
        if (onError) {
          onError(errors);
        }
      }
    );
  };

  userUpdate = (updates, onSuccess, onError) => {
    userUpdate(
      updates,
      (user) => {
        this.currentUser = user;
        if (onSuccess) {
          onSuccess(user);
        }
      },
      onError
    );
  };

  // Records that the user has seen a one-time callout (e.g. 'phone_feature').
  // Optimistically updates currentUser.seen_callouts so re-renders during the
  // in-flight request already treat the callout as dismissed, then reconciles
  // with the server payload on success.
  markCalloutSeen = (key, onSuccess, onError) => {
    if (!key) return;

    const nowIso = new Date().toISOString();
    if (this.currentUser) {
      this.currentUser.seen_callouts = {
        ...(this.currentUser.seen_callouts || {}),
        [key]: nowIso,
      };
    }

    markCalloutSeen(
      key,
      (user) => {
        this.currentUser = user;
        if (onSuccess) onSuccess(user);
      },
      onError
    );
  };

  impersonate = (userId, onSuccess, onError) => {
    if (!this.isSuperUser()) {
      if (onError) onError('Not authorized to impersonate users');
      return;
    }

    impersonateUser(
      userId,
      (user, message) => {
        this.currentUser = user;
        identifyFullStoryUser(this.currentUser);
        onSuccess(user, message);
      },
      onError
    );
  };

  stopImpersonating = (onSuccess, onError) => {
    stopImpersonating((user, message) => {
      this.currentUser = user;
      this.currentUser.true_user = null;
      identifyFullStoryUser(this.currentUser);
      onSuccess(user, message);
    }, onError);
  };

  setUnreadNotificationCount = (newUnreadNotificationCount) => {
    if (!this.isLoggedIn()) {
      return;
    }

    if (this.getCurrentUser().current_profile === null) {
      return;
    }

    this.getCurrentUser().current_profile.unread_notification_count = newUnreadNotificationCount;
  };

  updateProfile = (params, successCallback) => {
    if (!this.isLoggedIn()) {
      return;
    }

    updateProfile(this.getCurrentUser().profile_id, params, (newProfile) => {
      this.getCurrentUser().current_profile = newProfile;
      if (successCallback) {
        successCallback(newProfile);
      }
    });
  };

  // Add a method to check if user is a super user
  isSuperUser = () => {
    if (!this.isLoggedIn()) {
      return false;
    }

    // If we're impersonating, check if the true user is a super user
    if (this.isImpersonating()) {
      return this.currentUser.true_user && this.currentUser.true_user.is_super_user === true;
    }

    // Otherwise, check the current user
    return this.currentUser.is_super_user === true;
  };

  // Update auth methods to use the same guards
  isImpersonating = () => {
    return (
      this.currentUser &&
      this.currentUser.true_user &&
      this.currentUser.true_user !== null &&
      this.currentUser.id !== this.currentUser.true_user.id
    );
  };
}
